// src/admin/components/HomePageConfig/RegularSection.jsx
import React, { useState } from "react";
import ColorSection from "./ColorSection";
import FontSizeSection from "./FontSizeSection";

export default function RegularSection({
  title,
  list = [],
  setList,
  fields = [],
  isHeroSection = false,
  hasImageUpload = true, // New prop to control image upload
  uploadImageFile,
  deleteImageFile,
  imagesBase,
  sectionColors = {},
  setSectionColors,
  sectionSettings = {},
  fontSizes = null,
  onFontSizeChange,
  useSectionFontSizes = false,
  onToggleSectionFontSizes = null,
  fontSizeFields = []
}) {
  
  // Provide safe defaults
  const safeList = Array.isArray(list) ? list : [];
  const safeSectionColors = {
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
    buttonTextColor: "#ffffff",
    buttonBgColor: "#000000",
    ...sectionColors
  };

  const safeSectionSettings = {
    useSectionColors: true,
    ...sectionSettings
  };

  const safeFontSizes = fontSizes || {};

  const [localItems, setLocalItems] = useState(
    safeList.map(item => ({ 
      _id: Math.random().toString(36).slice(2), 
      ...item 
    }))
  );

  const handleColorChange = (property, value) => {
    if (setSectionColors) {
      setSectionColors(prev => ({
        ...(prev || {}),
        [property]: value === "transparent" ? "transparent" : value
      }));
    }
  };

  const toggleSectionColors = (useSectionColors) => {
    if (!sectionSettings) return;

    // If sectionSettings is a state setter
    if (typeof sectionSettings === "function") {
      sectionSettings((prev) => ({ ...prev, useSectionColors }));
      return;
    }

    // If sectionSettings is an object with a direct setter
    if (typeof sectionSettings.setUseSectionColors === "function") {
      sectionSettings.setUseSectionColors(useSectionColors);
      return;
    }

    // Fallback for other setter patterns
    const setterKey = Object.keys(sectionSettings).find((key) =>
      key.startsWith("set")
    );
    const setter = setterKey ? sectionSettings[setterKey] : null;
    if (typeof setter === "function") {
      setter((prev) => ({ ...prev, useSectionColors }));
    }
  };

  const addItem = () => {
    const newItem = {
      _id: Math.random().toString(36).slice(2),
      ...fields.reduce((acc, field) => {
        acc[field.key] = "";
        return acc;
      }, {})
    };
    
    // Add image field if hasImageUpload is true
    if (hasImageUpload) {
      newItem.image = "";
    }
    
    // Add hero-specific fields
    if (isHeroSection) {
      newItem.contentAlignment = "left";
      newItem.overlayOpacity = 0.3;
      newItem.titleColor = "#ffffff";
      newItem.contentColor = "#ffffff";
      newItem.buttonColor = "#ffffff";
      newItem.buttonBgColor = "#000000";
    }
    
    setLocalItems([...localItems, newItem]);
  };

  const removeItem = (id) => {
    const item = localItems.find(item => item._id === id);
    // Delete image if exists
    if (item && item.image) {
      deleteImageFile(item.image);
    }
    setLocalItems(localItems.filter(item => item._id !== id));
  };

  const updateItem = (id, field, value) => {
    setLocalItems(localItems.map(item => 
      item._id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleImageUpload = async (file, itemId) => {
    if (!file) return;
    const filename = await uploadImageFile(file);
    if (filename) {
      updateItem(itemId, "image", filename);
    }
  };

  const handleImageDelete = async (itemId) => {
    const item = localItems.find(i => i._id === itemId);
    if (item && item.image) {
      const ok = await deleteImageFile(item.image);
      if (ok) {
        updateItem(itemId, "image", "");
      }
    }
  };

  // Update parent list when local items change
  React.useEffect(() => {
    const itemsWithoutIds = localItems.map(({ _id, ...rest }) => rest);
    setList(itemsWithoutIds);
  }, [localItems, setList]);

  return (
    <div className="hcfg-card">
      <div className="hcfg-headrow">
        <h3>{title}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          {isHeroSection && (
            <div style={{ 
              backgroundColor: "#e3f2fd", 
              padding: "4px 10px", 
              borderRadius: "4px",
              fontSize: "12px",
              color: "#1565c0"
            }}>
              Hero Section
            </div>
          )}
          <button
            onClick={addItem}
            style={{
              padding: "8px 16px",
              background: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            + Add Item
          </button>
        </div>
      </div>

      {/* Items List */}
      <div style={{ marginTop: "20px" }}>
        {localItems.length === 0 ? (
          <div style={{ 
            padding: "20px", 
            textAlign: "center", 
            background: "#f5f5f5", 
            borderRadius: "4px",
            color: "#666"
          }}>
            No items added yet. Click "Add Item" to create one.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {localItems.map((item, index) => (
              <div key={item._id} style={{ 
                padding: "15px", 
                border: "1px solid #eee", 
                borderRadius: "4px",
                background: "#f9f9f9"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                  <h5 style={{ margin: 0 }}>{title} Item #{index + 1}</h5>
                  <button
                    onClick={() => removeItem(item._id)}
                    style={{
                      padding: "4px 12px",
                      background: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div className="hcfg-row">
                  {/* Image Column - for hasImageUpload or hero section */}
                  {(hasImageUpload || isHeroSection) && (
                    <div className="hcfg-col img">
                      <h6 style={{ marginBottom: "5px" }}>Image</h6>
                      {item.image ? (
                        <img
                          src={`${imagesBase}/${encodeURIComponent(item.image)}`}
                          alt="Preview"
                          style={{ 
                            width: "100%", 
                            maxHeight: "150px", 
                            objectFit: "cover",
                            borderRadius: "4px"
                          }}
                        />
                      ) : (
                        <div style={{
                          height: "100px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#e0e0e0",
                          borderRadius: "4px",
                          color: "#666",
                          fontSize: "12px"
                        }}>
                          No image
                        </div>
                      )}
                      <div className="hcfg-actions-inline" style={{ marginTop: "10px" }}>
                        <label className="hcfg-btn" style={{
                          padding: "6px 12px",
                          background: "#4CAF50",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          display: "inline-block",
                          fontSize: "12px"
                        }}>
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e.target.files?.[0], item._id)}
                            style={{ display: "none" }}
                          />
                        </label>
                        {item.image && (
                          <button
                            className="hcfg-btn danger"
                            onClick={() => handleImageDelete(item._id)}
                            style={{
                              padding: "6px 12px",
                              background: "#f44336",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              marginLeft: "5px",
                              fontSize: "12px"
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>

                      {/* Hero-specific settings */}
                      {isHeroSection && (
                        <>
                          <div style={{ marginTop: "10px" }}>
                            <label style={{ fontSize: "12px", display: "block", marginBottom: "5px" }}>Content Alignment</label>
                            <select
                              value={item.contentAlignment || "left"}
                              onChange={(e) => updateItem(item._id, "contentAlignment", e.target.value)}
                              style={{ width: "100%", padding: "6px", fontSize: "14px" }}
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                          </div>

                          <div style={{ marginTop: "10px" }}>
                            <label style={{ fontSize: "12px", display: "block", marginBottom: "5px" }}>Overlay Opacity</label>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={item.overlayOpacity || 0.3}
                                onChange={(e) => updateItem(item._id, "overlayOpacity", parseFloat(e.target.value))}
                                style={{ flex: 1 }}
                              />
                              <span style={{ minWidth: "30px", textAlign: "center", fontSize: "12px" }}>
                                {Number(item.overlayOpacity || 0.3).toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Dynamic fields */}
                  {fields.map((field, fieldIndex) => (
                    <div key={field.key} className="hcfg-col">
                      <label style={{ fontSize: "12px", display: "block", marginBottom: "5px" }}>
                        {field.label}
                      </label>
                      {field.type === "textarea" ? (
                        <textarea
                          rows={field.rows || 3}
                          value={item[field.key] || ""}
                          onChange={(e) => updateItem(item._id, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          style={{ 
                            width: "100%", 
                            padding: "8px", 
                            fontSize: "14px", 
                            resize: "vertical",
                            minHeight: `${(field.rows || 3) * 24}px`
                          }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={item[field.key] || ""}
                          onChange={(e) => updateItem(item._id, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          style={{ width: "100%", padding: "8px", fontSize: "14px" }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Hero-specific color fields */}
                {isHeroSection && (
                  <div className="hcfg-row" style={{ marginTop: "15px", paddingTop: "15px", borderTop: "1px solid #ddd" }}>
                    <div className="hcfg-col">
                      <label style={{ fontSize: "12px" }}>Title Color</label>
                      <input
                        type="color"
                        value={item.titleColor || "#ffffff"}
                        onChange={(e) => updateItem(item._id, "titleColor", e.target.value)}
                        style={{ width: "100%", height: "40px" }}
                      />
                      <input
                        type="text"
                        value={item.titleColor || "#ffffff"}
                        onChange={(e) => updateItem(item._id, "titleColor", e.target.value)}
                        style={{ width: "100%", padding: "4px", fontSize: "12px", marginTop: "5px" }}
                      />
                    </div>
                    <div className="hcfg-col">
                      <label style={{ fontSize: "12px" }}>Content Color</label>
                      <input
                        type="color"
                        value={item.contentColor || "#ffffff"}
                        onChange={(e) => updateItem(item._id, "contentColor", e.target.value)}
                        style={{ width: "100%", height: "40px" }}
                      />
                      <input
                        type="text"
                        value={item.contentColor || "#ffffff"}
                        onChange={(e) => updateItem(item._id, "contentColor", e.target.value)}
                        style={{ width: "100%", padding: "4px", fontSize: "12px", marginTop: "5px" }}
                      />
                    </div>
                    <div className="hcfg-col">
                      <label style={{ fontSize: "12px" }}>Button Color</label>
                      <input
                        type="color"
                        value={item.buttonColor || "#ffffff"}
                        onChange={(e) => updateItem(item._id, "buttonColor", e.target.value)}
                        style={{ width: "100%", height: "40px" }}
                      />
                      <input
                        type="text"
                        value={item.buttonColor || "#ffffff"}
                        onChange={(e) => updateItem(item._id, "buttonColor", e.target.value)}
                        style={{ width: "100%", padding: "4px", fontSize: "12px", marginTop: "5px" }}
                      />
                    </div>
                    <div className="hcfg-col">
                      <label style={{ fontSize: "12px" }}>Button Background</label>
                      <input
                        type="color"
                        value={item.buttonBgColor || "#000000"}
                        onChange={(e) => updateItem(item._id, "buttonBgColor", e.target.value)}
                        style={{ width: "100%", height: "40px" }}
                      />
                      <input
                        type="text"
                        value={item.buttonBgColor || "#000000"}
                        onChange={(e) => updateItem(item._id, "buttonBgColor", e.target.value)}
                        style={{ width: "100%", padding: "4px", fontSize: "12px", marginTop: "5px" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Color Customization for Section */}
      {sectionColors && (
        <ColorSection
          title={title}
          colors={safeSectionColors}
          onColorChange={handleColorChange}
          useSectionColors={safeSectionSettings.useSectionColors !== false}
          onToggleSectionColors={toggleSectionColors}
        />
      )}

      {fontSizeFields.length > 0 && onFontSizeChange && (
        <FontSizeSection
          title={title}
          sizes={safeFontSizes}
          onChange={onFontSizeChange}
          useSectionFontSizes={useSectionFontSizes}
          onToggleSectionFontSizes={onToggleSectionFontSizes}
          fields={fontSizeFields}
        />
      )}
    </div>
  );
}
